import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, Trash2, Pencil, Zap, AlertCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import AdminLayout from '../../components/admin/AdminLayout';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const emptyForm = {
  name: '', multiplier: 1.5, days_of_week: [], hour_start: '', hour_end: '',
  enabled: true, priority: 0, reason: '',
};

const AdminSurgePricing = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const api = (await import('../../utils/api')).default;
      const { data } = await api.get('/admin/surge-rules');
      setRules(data.items || []);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load surge rules', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRules(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setShowDialog(true); };
  const openEdit = (r) => {
    setEditing(r.id);
    setForm({
      name: r.name || '', multiplier: r.multiplier, days_of_week: r.days_of_week || [],
      hour_start: r.hour_start ?? '', hour_end: r.hour_end ?? '',
      enabled: r.enabled, priority: r.priority || 0, reason: r.reason || '',
    });
    setShowDialog(true);
  };

  const toggleDay = (idx) => {
    setForm(f => ({
      ...f,
      days_of_week: f.days_of_week.includes(idx)
        ? f.days_of_week.filter(d => d !== idx)
        : [...f.days_of_week, idx].sort(),
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast({ title: 'Name required', variant: 'destructive' }); return; }
    const payload = {
      name: form.name.trim(),
      multiplier: parseFloat(form.multiplier),
      days_of_week: form.days_of_week.length ? form.days_of_week : null,
      hour_start: form.hour_start === '' ? null : parseInt(form.hour_start, 10),
      hour_end: form.hour_end === '' ? null : parseInt(form.hour_end, 10),
      enabled: form.enabled,
      priority: parseInt(form.priority, 10) || 0,
      reason: form.reason.trim() || null,
    };
    try {
      const api = (await import('../../utils/api')).default;
      if (editing) {
        await api.put(`/admin/surge-rules/${editing}`, payload);
        toast({ title: 'Surge rule updated' });
      } else {
        await api.post('/admin/surge-rules', payload);
        toast({ title: 'Surge rule created' });
      }
      setShowDialog(false);
      fetchRules();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Save failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this surge rule?')) return;
    try {
      const api = (await import('../../utils/api')).default;
      await api.delete(`/admin/surge-rules/${id}`);
      toast({ title: 'Surge rule deleted' });
      fetchRules();
    } catch (err) {
      toast({ title: 'Error', description: 'Delete failed', variant: 'destructive' });
    }
  };

  const toggleEnabled = async (rule) => {
    try {
      const api = (await import('../../utils/api')).default;
      await api.put(`/admin/surge-rules/${rule.id}`, { enabled: !rule.enabled });
      fetchRules();
    } catch {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const formatDays = (dow) => !dow || dow.length === 0 ? 'All days' : dow.map(d => DAYS[d]).join(', ');
  const formatHours = (s, e) => (s === null || s === undefined) || (e === null || e === undefined)
    ? 'Any time'
    : `${String(s).padStart(2, '0')}:00 – ${String(e).padStart(2, '0')}:00`;

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="admin-surge-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Zap className="h-6 w-6 text-pink-600" /> Surge Pricing
            </h1>
            <p className="text-sm text-gray-500">Configure dynamic price multipliers for peak hours, weekdays, or special events.</p>
          </div>
          <Button onClick={openNew} className="bg-pink-600 hover:bg-pink-700" data-testid="add-surge-rule-btn">
            <Plus className="h-4 w-4 mr-1" /> New Rule
          </Button>
        </div>

        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="flex gap-2 items-start">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-medium">How surge works</p>
              <p>Highest-priority enabled rule that matches the current day/hour/ride type wins. Multiplier is applied to total fare at booking time. Default multiplier is 1.0× (no surge).</p>
            </div>
          </div>
        </Card>

        {loading && <p className="text-gray-500">Loading...</p>}

        {!loading && rules.length === 0 && (
          <Card className="p-8 text-center text-gray-500">
            <Zap className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No surge rules configured. Click "New Rule" to create your first one.</p>
          </Card>
        )}

        <div className="space-y-3" data-testid="surge-rules-list">
          {rules.map(r => (
            <Card key={r.id} className="p-4">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900">{r.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${r.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {r.enabled ? 'Active' : 'Disabled'}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 font-bold">{r.multiplier}×</span>
                    {r.priority > 0 && <span className="text-xs text-gray-500">Priority {r.priority}</span>}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {formatDays(r.days_of_week)} · {formatHours(r.hour_start, r.hour_end)}
                  </div>
                  {r.reason && <p className="text-xs text-gray-500 mt-1 italic">{r.reason}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={r.enabled} onCheckedChange={() => toggleEnabled(r)} data-testid={`surge-toggle-${r.id}`} />
                  <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`surge-edit-${r.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(r.id)} data-testid={`surge-delete-${r.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-lg" data-testid="surge-rule-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit' : 'New'} Surge Rule</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Friday Evening Rush" data-testid="surge-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Multiplier ({form.multiplier}×)</Label>
                  <input
                    type="range" min="1.0" max="3.0" step="0.1"
                    value={form.multiplier}
                    onChange={e => setForm({ ...form, multiplier: e.target.value })}
                    className="w-full"
                    data-testid="surge-multiplier-input"
                  />
                </div>
                <div>
                  <Label>Priority</Label>
                  <Input type="number" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Days of week (none = all days)</Label>
                <div className="flex flex-wrap gap-1 mt-1" data-testid="surge-days-picker">
                  {DAYS.map((d, idx) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggleDay(idx)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border ${form.days_of_week.includes(idx) ? 'bg-pink-600 text-white border-pink-600' : 'bg-white text-gray-700 border-gray-300'}`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Hour start (0-23)</Label>
                  <Input type="number" min="0" max="23" value={form.hour_start} onChange={e => setForm({ ...form, hour_start: e.target.value })} placeholder="17" />
                </div>
                <div>
                  <Label>Hour end (exclusive)</Label>
                  <Input type="number" min="0" max="23" value={form.hour_end} onChange={e => setForm({ ...form, hour_end: e.target.value })} placeholder="20" />
                </div>
              </div>
              <div>
                <Label>Reason (shown to riders)</Label>
                <Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Friday rush hour" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
                <Label className="!mb-0">Enabled</Label>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button className="flex-1 bg-pink-600 hover:bg-pink-700" onClick={handleSave} data-testid="surge-save-btn">Save</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminSurgePricing;
